/**
 * FeaturedPropertiesEditor Component
 * 
 * Property editor for Featured block type.
 * Allows selecting specific menu items and configuring display.
 */

import React, { memo, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FeaturedBlockProps } from '@/types/menuBuilder';
import { useBranch } from '@/contexts/BranchContext';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface MenuItem {
  id: string;
  name: string;
  category?: { name: string };
}

interface FeaturedPropertiesEditorProps {
  props: FeaturedBlockProps;
  onUpdate: (props: Partial<FeaturedBlockProps>) => void;
}

export const FeaturedPropertiesEditor = memo(function FeaturedPropertiesEditor({
  props,
  onUpdate,
}: FeaturedPropertiesEditorProps) {
  const { t } = useTranslation();
  const { selectedBranchId } = useBranch();
  const [isItemSelectorOpen, setIsItemSelectorOpen] = useState(false);

  // Fetch all menu items for this branch
  const { data: allItems = [] } = useQuery({
    queryKey: ['all-menu-items', selectedBranchId],
    queryFn: async () => {
      if (!selectedBranchId) return [];
      const { data, error } = await supabase
        .from('menu_items')
        .select('*, category:categories(name)')
        .eq('categories.branch_id', selectedBranchId);
      if (error) throw error;
      return (data || []) as MenuItem[];
    },
    enabled: !!selectedBranchId,
  });

  // Get selected items details
  const selectedItems = useMemo(() => {
    return (props.itemIds || [])
      .map((id) => allItems.find((item) => item.id === id))
      .filter(Boolean) as MenuItem[];
  }, [props.itemIds, allItems]);

  const handleRemoveItem = (itemId: string) => {
    onUpdate({
      itemIds: (props.itemIds || []).filter((id) => id !== itemId),
    });
  };

  const handleToggleItem = (itemId: string) => {
    const currentIds = props.itemIds || [];
    if (currentIds.includes(itemId)) {
      onUpdate({ itemIds: currentIds.filter((id) => id !== itemId) });
    } else {
      onUpdate({ itemIds: [...currentIds, itemId] });
    }
  };

  return (
    <div className="space-y-6">
      {/* Show Title */}
      <div className="flex items-center justify-between">
        <Label htmlFor="showTitle">
          {t('menuBuilder.showTitle', 'Show Title')}
        </Label>
        <Switch
          id="showTitle"
          checked={props.showTitle}
          onCheckedChange={(checked) => onUpdate({ showTitle: checked })}
        />
      </div>

      {/* Title */}
      {props.showTitle && (
        <div className="space-y-2">
          <Label>{t('menuBuilder.sectionTitle', 'Section Title')}</Label>
          <Input
            value={props.title || ''}
            onChange={(e) => onUpdate({ title: e.target.value })}
            placeholder={t('menuBuilder.titlePlaceholder', 'e.g., Chef\'s Specials')}
          />
        </div>
      )}

      {/* Title Alignment */}
      {props.showTitle && (
        <div className="space-y-2">
          <Label>{t('menuBuilder.titleAlignment', 'Title Alignment')}</Label>
          <Select
            value={props.titleAlignment || 'left'}
            onValueChange={(value) =>
              onUpdate({ titleAlignment: value as FeaturedBlockProps['titleAlignment'] })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left">{t('common.left', 'Left')}</SelectItem>
              <SelectItem value="center">{t('common.center', 'Center')}</SelectItem>
              <SelectItem value="right">{t('common.right', 'Right')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Selected Items */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.selectedItems', 'Selected Items')}</Label>
        <div className="space-y-2">
          {selectedItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('menuBuilder.noItemsSelected', 'No items selected')}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {selectedItems.map((item) => (
                <Badge
                  key={item.id}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  {item.name}
                  <button
                    onClick={() => handleRemoveItem(item.id)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <Dialog open={isItemSelectorOpen} onOpenChange={setIsItemSelectorOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                {t('menuBuilder.addItems', 'Add Items')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {t('menuBuilder.selectItems', 'Select Items')}
                </DialogTitle>
              </DialogHeader>
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-2">
                  {allItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 p-2 rounded hover:bg-muted"
                    >
                      <Checkbox
                        id={item.id}
                        checked={(props.itemIds || []).includes(item.id)}
                        onCheckedChange={() => handleToggleItem(item.id)}
                      />
                      <label
                        htmlFor={item.id}
                        className="flex-1 cursor-pointer"
                      >
                        <span className="font-medium">{item.name}</span>
                        {item.category && (
                          <span className="text-xs text-muted-foreground ml-2">
                            ({item.category.name})
                          </span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Layout */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.layoutMode', 'Layout')}</Label>
        <Select
          value={props.layout}
          onValueChange={(value) =>
            onUpdate({ layout: value as FeaturedBlockProps['layout'] })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="grid">{t('menuBuilder.grid', 'Grid')}</SelectItem>
            <SelectItem value="carousel">
              {t('menuBuilder.carousel', 'Carousel')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Columns (for grid) */}
      {props.layout === 'grid' && (
        <div className="space-y-2">
          <Label>{t('menuBuilder.columns', 'Columns')}</Label>
          <Select
            value={String(props.columns || 2)}
            onValueChange={(value) => onUpdate({ columns: Number(value) as 1 | 2 | 3 })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1</SelectItem>
              <SelectItem value="2">2</SelectItem>
              <SelectItem value="3">3</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Carousel Options */}
      {props.layout === 'carousel' && (
        <>
          <div className="flex items-center justify-between">
            <Label htmlFor="autoplay">
              {t('menuBuilder.autoplay', 'Autoplay')}
            </Label>
            <Switch
              id="autoplay"
              checked={props.autoplay}
              onCheckedChange={(checked) => onUpdate({ autoplay: checked })}
            />
          </div>

          {props.autoplay && (
            <div className="space-y-2">
              <Label>{t('menuBuilder.interval', 'Interval (seconds)')}</Label>
              <Input
                type="number"
                min={2}
                max={15}
                value={props.interval || 5}
                onChange={(e) => onUpdate({ interval: Number(e.target.value) || 5 })}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
});

export default FeaturedPropertiesEditor;
